import { useState, useEffect, useCallback, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { X, Sparkles, ChevronLeft } from 'lucide-react';
import { useAssistantPanel } from '../../contexts/AssistantPanelContext';
import { useAuth } from '@/contexts/AuthContext';
import { isAiAssistantEnabled, useFeatures } from '@/contexts/FeaturesContext';
import { getConversation } from '../../api/assistant';
import { ConversationList } from '../chat/ConversationList';
import { ChatView } from '../chat/ChatView';
import { ModelSelector } from '../chat/ModelSelector';
import { UsageBadge } from './UsageBadge';

const DEFAULT_WIDTH = 380;
const MIN_WIDTH = 320;
const WIDTH_STORAGE_KEY = 'assistant-panel-width';

function loadStoredWidth(): number {
  const stored = Number(localStorage.getItem(WIDTH_STORAGE_KEY));
  return Number.isFinite(stored) && stored > 0 ? stored : DEFAULT_WIDTH;
}

function clampWidth(width: number): number {
  const maxWidth = Math.max(MIN_WIDTH, Math.min(900, window.innerWidth * 0.9));
  return Math.min(Math.max(width, MIN_WIDTH), maxWidth);
}

export function AssistantPanel() {
  const {
    isOpen,
    closeAssistant,
    selectedConversationId,
    setSelectedConversationId,
  } = useAssistantPanel();
  const { isAuthenticated, user } = useAuth();
  const { isEnabled } = useFeatures();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [width, setWidth] = useState(() => clampWidth(loadStoredWidth()));
  const [isResizing, setIsResizing] = useState(false);
  const resizeStartRef = useRef<{ startX: number; startWidth: number } | null>(null);
  // `inert` removes the closed panel from the tab order and the accessibility tree.
  // Set through the DOM because React 18 does not forward the attribute.
  const panelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = panelRef.current;
    if (!el) return;
    if (isOpen) el.removeAttribute('inert');
    else el.setAttribute('inert', '');
  }, [isOpen]);

  const { data: conversationData } = useQuery({
    queryKey: ['assistant-conversation', selectedConversationId],
    queryFn: () => getConversation(selectedConversationId!),
    enabled: !!selectedConversationId,
  });

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      setWidth((w) => clampWidth(w));
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleDragStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    resizeStartRef.current = { startX: e.clientX, startWidth: width };
    setIsResizing(true);
  }, [width]);

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      const start = resizeStartRef.current;
      if (!start) return;
      // Panel is anchored to the left edge, so dragging right grows it.
      setWidth(clampWidth(start.startWidth + (e.clientX - start.startX)));
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      resizeStartRef.current = null;
      setWidth((w) => {
        localStorage.setItem(WIDTH_STORAGE_KEY, String(w));
        return w;
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  if (!isAuthenticated) return null;
  if (!isAiAssistantEnabled(user?.role, isEnabled)) return null;

  // Unlike the desktop `width` state (clamped via clampWidth on every resize), this hardcoded
  // 380 was never checked against the actual viewport — on a narrower window/phone the panel
  // overflowed off the right edge, cutting off the close button that sits there.
  const panelWidth = isMobile ? Math.min(380, window.innerWidth) : width;

  return (
    <div
      style={{ width: panelWidth }}
      ref={panelRef}
      data-testid="assistant-panel"
      aria-hidden={!isOpen}
      className={`fixed top-0 left-0 h-full bg-background border-r shadow-xl z-50 flex flex-col ease-in-out ${
        isResizing ? '' : 'transition-all duration-300'
      } ${isOpen ? 'translate-x-0 visible' : '-translate-x-full invisible'}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b bg-background">
        <div className="flex items-center gap-2">
          {selectedConversationId && (
            <Button
              variant="ghost"
              size="sm"
              aria-label="Back to assistant conversations"
              data-agent-target="assistant-back-button"
              className="h-8 w-8 p-0"
              onClick={() => setSelectedConversationId(null)}
              data-testid="assistant-back-button"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
          )}
          <Sparkles className="h-5 w-5 text-foreground" />
          <span className="font-semibold">AI Assistant</span>
        </div>
        <div className="flex items-center gap-2">
          <UsageBadge />
          {selectedConversationId && (
            <ModelSelector
              conversationId={selectedConversationId}
              currentModelId={conversationData?.conversation?.modelId}
            />
          )}
          <Button
            variant="ghost"
            size="sm"
            aria-label="Close AI assistant"
            data-agent-target="assistant-close-button"
            className="h-8 w-8 p-0"
            onClick={closeAssistant}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Conversation list (hidden when a conversation is selected) */}
      <div className={`flex-1 overflow-hidden flex flex-col ${selectedConversationId ? 'hidden' : ''}`}>
        <ConversationList />
      </div>

      {/* Chat view (shown when a conversation is selected) */}
      {selectedConversationId && (
        <div className="flex-1 overflow-hidden flex flex-col">
          <ChatView conversationId={selectedConversationId} />
        </div>
      )}

      {/* Drag handle to resize the panel — desktop only, doesn't make sense on mobile widths */}
      {!isMobile && (
        <div
          onMouseDown={handleDragStart}
          className={`absolute top-0 right-0 h-full w-1.5 cursor-col-resize hover:bg-primary/30 ${
            isResizing ? 'bg-primary/40' : 'bg-transparent'
          }`}
          style={{ transform: 'translateX(50%)' }}
        />
      )}
    </div>
  );
}
